import { useEffect, useId, useRef, useState } from 'react';
import { AlertCircle, LoaderCircle } from 'lucide-react';
import {
  MediaControlBar,
  MediaController,
  MediaDurationDisplay,
  MediaMuteButton,
  MediaPlaybackRateButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome/react';

interface EpisodeAudioPlayerProps {
  src: string;
  title: string;
  startAtSeconds?: number | null;
}

const PLAY_EVENT = 'episode-audio-play';

export default function EpisodeAudioPlayer({
  src,
  title,
  startAtSeconds,
}: EpisodeAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const playerId = useId();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading'
  );

  useEffect(() => setStatus('loading'), [src]);

  useEffect(() => {
    const pauseOtherPlayer = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== playerId) {
        audioRef.current?.pause();
      }
    };

    window.addEventListener(PLAY_EVENT, pauseOtherPlayer);
    return () => window.removeEventListener(PLAY_EVENT, pauseOtherPlayer);
  }, [playerId]);

  const seekToStart = () => {
    if (audioRef.current && startAtSeconds != null) {
      audioRef.current.currentTime = Math.max(0, startAtSeconds);
    }
  };

  const announcePlayback = () => {
    window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: playerId }));
  };

  return (
    <div className="mt-4">
      {status === 'loading' && (
        <div
          role="status"
          className="mb-2 flex items-center gap-2 text-sm text-base-content/70"
        >
          <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading audio...
        </div>
      )}
      {status === 'error' && (
        <div
          role="alert"
          className="mb-2 flex items-center gap-2 text-sm text-error"
        >
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          Audio is currently unavailable.
        </div>
      )}
      <MediaController
        audio
        className="block w-full overflow-hidden rounded-lg border border-base-300 bg-base-200 text-base-content"
        aria-label={`Audio player for ${title}`}
      >
        <audio
          ref={audioRef}
          slot="media"
          title={title}
          src={src}
          preload="metadata"
          autoPlay
          onLoadStart={() => setStatus('loading')}
          onLoadedMetadata={seekToStart}
          onWaiting={() => setStatus('loading')}
          onSeeking={() => setStatus('loading')}
          onCanPlay={() => setStatus('ready')}
          onPlaying={() => setStatus('ready')}
          onError={() => setStatus('error')}
          onPlay={announcePlayback}
        />
        <MediaControlBar className="flex min-h-12 w-full items-center gap-1 overflow-hidden bg-base-200 px-2 py-1">
          <MediaPlayButton />
          <MediaSeekBackwardButton seekOffset={15} />
          <MediaSeekForwardButton seekOffset={15} />
          <MediaTimeDisplay className="hidden sm:inline-flex" />
          <MediaTimeRange className="mx-1 min-w-16 flex-1" />
          <MediaDurationDisplay className="hidden sm:inline-flex" />
          <MediaMuteButton className="hidden sm:inline-flex" />
          <MediaVolumeRange className="hidden w-24 md:inline-flex" />
          <MediaPlaybackRateButton rates={[0.75, 1, 1.25, 1.5, 1.75, 2]} />
        </MediaControlBar>
      </MediaController>
    </div>
  );
}
